import * as React                             from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import DV                                     from 'ts-data-validator';
import { LocalStorageService }                from 'ts-storage-service';

import { AccessChecker } from '../../../common/services/accessChecker';

import useRequest from '../../common/services/request';
import useStorage from '../../common/services/storage';

import { useCatalog } from '../catalog';
import { useCommon }  from '../common';

import userConstants    from './user.constants';
import UserContext      from './user.context';
import userReducer      from './user.reducer';
import userInitialState from './user.state';

function UserProvider(props) {
  const history  = useHistory();
  const location = useLocation();

  const {
          children,
          value,
        } = props;

  const catalog = useCatalog();
  const common  = useCommon();
  const request = useRequest();
  const storage = useStorage(userConstants.STORAGE);

  const ls = new LocalStorageService('user');

  const [state, dispatch] = React.useReducer(userReducer, {
    ...userInitialState,
    location:     value.location || userInitialState.location,
    probablyCity: value.probablyCity || userInitialState.probablyCity,
  });

  const addToBookmarks      = React.useCallback((offer) => {
    const currentBookmarks = ls.getData('bookmarks') || {};
    const modelBookmarks   = currentBookmarks[offer.modelId] || {};
    const bookmarkKey      = `_${offer.offerId}`;

    if (modelBookmarks[bookmarkKey]) {
      delete modelBookmarks[bookmarkKey];

      if (!DV.isNotEmptyObject(modelBookmarks)) {
        delete currentBookmarks[offer.modelId];
      }
    } else {
      modelBookmarks[bookmarkKey]     = { common: offer };
      currentBookmarks[offer.modelId] = modelBookmarks;
    }

    const currentDate  = new Date();
    const oneYearLater = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));

    ls.setData('bookmarks', currentBookmarks, oneYearLater);

    dispatch({
      bookmarks: currentBookmarks,
      type:      userConstants.ADD_TO_BOOKMARKS_SUCCESS,
    });
  }, [dispatch]);
  const changePassword      = React.useCallback((values) => {
    dispatch({ type: userConstants.CHANGE_PASSWORD_REQUEST });

    request.post('/change-password', { values })
      .then((response) => {
        dispatch({
          type: userConstants.CHANGE_PASSWORD_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        dispatch({
          type: userConstants.CHANGE_PASSWORD_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const clearBookmarks      = React.useCallback(() => {
    ls.removeData('bookmarks');

    dispatch({ type: userConstants.REMOVE_BOOKMARKS });
  }, [dispatch]);
  const getAuthToken        = React.useCallback(() => {
    dispatch({ type: userConstants.GET_AUTH_TOKEN_REQUEST });

    request.get(`/get-auth-token?t=${new Date().getTime()}`)
      .then((response) => {
        dispatch({
          type: userConstants.GET_AUTH_TOKEN_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        dispatch({
          type: userConstants.GET_AUTH_TOKEN_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const getGeolocation      = React.useCallback(() => {
    dispatch({ type: userConstants.USER_GET_LOCATION_REQUEST });

    request.get('/get-user-geolocation')
      .then((response) => {
        dispatch({
          type: userConstants.USER_GET_LOCATION_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        dispatch({
          type: userConstants.USER_GET_LOCATION_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const getProfileData      = React.useCallback(() => {
    dispatch({ type: userConstants.PROFILE_GET_INITIAL_STATE_REQUEST });

    return request.get('/api/profile')
      .then((response) => {
        return dispatch({
          type: userConstants.PROFILE_GET_INITIAL_STATE_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        return dispatch({
          type: userConstants.PROFILE_GET_INITIAL_STATE_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const getReactivationLink = React.useCallback((email) => {
    dispatch({ type: userConstants.GET_REACTIVATION_LINK_REQUEST });

    request.get(`/get-reactivation-link?email=${email}`)
      .then((response) => {
        dispatch({
          type: userConstants.GET_REACTIVATION_LINK_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        dispatch({
          type: userConstants.GET_REACTIVATION_LINK_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const getToken            = React.useCallback((appealType) => {
    dispatch({ type: userConstants.GET_APPEAL_TOKEN_REQUEST });

    return request.get(`/appeal/index?tokenFor=${appealType}`)
      .then((response) => {
        return dispatch({
          appealType,
          type: userConstants.GET_APPEAL_TOKEN_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        return dispatch({ type: userConstants.GET_APPEAL_TOKEN_FAILURE, ...error });
      });
  }, [dispatch]);
  const getUserInfo         = React.useCallback(() => {
    dispatch({ type: userConstants.GET_USER_INFO_REQUEST });

    const timezone = Intl
      .DateTimeFormat()
      .resolvedOptions()
      .timeZone;

    let promise = storage.hasData()
      ? new Promise((resolve, reject) => {
        const data = storage.checkData();

        if (
          !DV.isNull(data.location) &&
          DV.isObject(data.location) &&
          data.location.hasOwnProperty('cityId') &&
          data.location.hasOwnProperty('label') &&
          DV.isNotEmptyArray(data.regions) &&
          DV.isNotEmptyArray(data.cities)
        ) {
          resolve(storage.getData());
        } else {
          reject();
        }
      })
      : request.get(`/get-user-initial-state?tz=${timezone}`);

    return promise
      .then((response) => {
        storage.setData(response);

        AccessChecker.init({ ...response });

        dispatch({
          ...response,
          bookmarks: ls.getData('bookmarks'),
          type:      userConstants.GET_USER_INFO_SUCCESS,
        });
      })
      .catch((error) => {
        return dispatch({ type: userConstants.GET_USER_INFO_FAILURE, ...error });
      });
  }, [dispatch]);
  const logout              = React.useCallback(() => {
    dispatch({ type: userConstants.USER_LOGOUT_REQUEST });

    request.get('/logout')
      .then((response) => {
        storage.removeData();

        dispatch({
          type: userConstants.USER_LOGOUT_SUCCESS,
          ...response,
        });
      })
      .catch(() => {
      });
  }, [dispatch]);
  const register            = React.useCallback((values) => {
    dispatch({ type: userConstants.USER_REGISTER_REQUEST });

    return request.post('/register', values)
      .then((response) => {
        return dispatch({
          type: userConstants.USER_REGISTER_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        return dispatch({
          type: userConstants.USER_REGISTER_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const send                = React.useCallback((values, typeAppeal) => {
    dispatch({ type: userConstants.SEND_APPEAL_REQUEST });

    return request.post('/appeal/index', values)
      .then((response) => {
        return dispatch({
          type: userConstants.SEND_APPEAL_SUCCESS,
          typeAppeal,
          ...response,
        });
      })
      .catch((error) => {
        dispatch({
          type: userConstants.SEND_APPEAL_FAILURE,
          ...error,
        });

        return Promise.reject();
      });
  }, [dispatch]);
  const submitAccountData   = React.useCallback((values) => {
    dispatch({ type: userConstants.PROFILE_SUBMIT_ACCOUNT_DATA_REQUEST });

    request.post('/api/profile', values)
      .then((response) => {
        return dispatch({
          type: userConstants.PROFILE_SUBMIT_ACCOUNT_DATA_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        return dispatch({
          type: userConstants.PROFILE_SUBMIT_ACCOUNT_DATA_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const submitUserData      = React.useCallback((values) => {
    dispatch({ type: userConstants.PROFILE_SUBMIT_USER_DATA_REQUEST });

    return request.post('/api/profile', values)
      .then((response) => {
        return dispatch({
          type: userConstants.PROFILE_SUBMIT_USER_DATA_SUCCESS,
          ...response,
        });
      })
      .catch((error) => {
        return dispatch({
          type: userConstants.PROFILE_SUBMIT_USER_DATA_FAILURE,
          ...error,
        });
      });
  }, [dispatch]);
  const updatePage          = React.useCallback((place) => {
    const { params } = matchPath(location.pathname, {
      exact:  true,
      path:   `/:page/${DOOR_PREFIX}:door`,
      strict: false,
    }) ?? matchPath(location.pathname, {
      path:   '/:page/:urlKey?',
      exact:  true,
      strict: false,
    }) ?? {};

    if (
      [
        'vhodnye-dveri',
        'mezhkomnatnye-dveri',
      ].includes(params?.page)
    ) {
      if (params?.door) {
        catalog.getCatalogItem(location.pathname);
      } else {
        catalog.getCatalogItems(`${location.pathname}${location.search}`);
      }
    } else if (params?.page === 'gde-kupit-kontakty' && place) {
      history.replace(`/gde-kupit-kontakty/${place}`);
    }
  }, [location.pathname, location.search]);
  const changeLocation      = React.useCallback((city, place) => {
    dispatch({ type: userConstants.USER_CHANGE_LOCATION_REQUEST });

    return request.post('/set-user-location', city)
      .then((response) => {
        // Exclude bookmarks from response
        const {
                bookmarks,
                ...dataWithoutBookmarks
              } = response;

        storage.setData(dataWithoutBookmarks);
        common.onChangeLocation(dataWithoutBookmarks);

        dispatch({
          type: userConstants.USER_CHANGE_LOCATION_SUCCESS,
          ...dataWithoutBookmarks,
        });

        updatePage(place);

        localStorage.removeItem(userConstants.STORAGE);
      })
      .catch((error) => {
        return dispatch({
          type: userConstants.USER_CHANGE_LOCATION_FAILURE,
          ...error,
        });
      });
  }, [dispatch, updatePage]);
  const login               = React.useCallback((values, allowedCities) => {
    dispatch({ type: userConstants.USER_LOGIN_REQUEST });

    request.post('/login', values)
      .then((response) => {
        storage.setData(response);

        dispatch({
          type: userConstants.USER_LOGIN_SUCCESS,
          ...response,
        });

        getUserInfo()
          .then((response) => {
            const place = allowedCities.find(({ cityId }) => response.location.cityId === cityId);

            updatePage(place?.urlKey);
          });
      })
      .catch((error) => {
        dispatch({ type: userConstants.USER_LOGIN_FAILURE, ...error });
      });
  }, [dispatch, updatePage]);
  const userDelete          = React.useCallback(() => {
    dispatch({ type: userConstants.USER_DELETE_REQUEST });

    request.get('/user-delete')
      .then((response) => {
        storage.removeData();

        dispatch({ type: userConstants.USER_DELETE_SUCCESS, ...response });

        logout();
      })
      .catch((error) =>
        dispatch({ type: userConstants.USER_DELETE_FAILURE, ...error }),
      );
  }, [dispatch, logout]);

  const context = React.useMemo(() => {
    return {
      ...state,
      addToBookmarks,
      changeLocation,
      changePassword,
      getAuthToken,
      getGeolocation,
      getProfileData,
      getReactivationLink,
      getToken,
      getUserInfo,
      login,
      logout,
      register,
      send,
      submitAccountData,
      submitUserData,
      userDelete,
      clearBookmarks,
    };
  }, [
    addToBookmarks,
    changeLocation,
    changePassword,
    clearBookmarks,
    getAuthToken,
    getGeolocation,
    getProfileData,
    getReactivationLink,
    getToken,
    getUserInfo,
    login,
    logout,
    register,
    send,
    state,
    submitAccountData,
    submitUserData,
    userDelete,
  ]);

  return (
    <UserContext.Provider value={context}>
      {children}
    </UserContext.Provider>
  );
}

export default UserProvider;
